import { z } from 'zod';
import { ENERGY_PRICE_ZONES } from '@casa/driver-sdk';
import { secret, secretKeys } from '@casa/model';

// The Uconnect brands, as the form offers them (the driver's table: `uconnect.ts`, UCONNECT_BRANDS).
const UCONNECT_BRAND_LABELS = {
    FIAT_EU: 'Fiat, Europe', FIAT_US: 'Fiat, US', FIAT_CANADA: 'Fiat, Canada', FIAT_ASIA: 'Fiat, Asia',
    ALFA_ROMEO_EU: 'Alfa Romeo, Europe', ALFA_ROMEO_US_CANADA: 'Alfa Romeo, US and Canada', ALFA_ROMEO_ASIA: 'Alfa Romeo, Asia',
    JEEP_EU: 'Jeep, Europe', JEEP_US: 'Jeep, US', JEEP_ASIA: 'Jeep, Asia',
    MASERATI_EU: 'Maserati, Europe', MASERATI_US_CANADA: 'Maserati, US and Canada', MASERATI_ASIA: 'Maserati, Asia',
    CHRYSLER_US: 'Chrysler, US', CHRYSLER_CANADA: 'Chrysler, Canada', DODGE_US: 'Dodge, US', RAM_US: 'Ram, US',
} as const;
export const UCONNECT_BRAND_NAMES = Object.keys(UCONNECT_BRAND_LABELS) as [keyof typeof UCONNECT_BRAND_LABELS, ...(keyof typeof UCONNECT_BRAND_LABELS)[]];

// The myVAILLANT brands and the countries of those with a login per country (the driver's realms:
// `myvaillant.ts`, realmOf; myPyllant's `const.py`, as of 2026-10-31).
const MYVAILLANT_BRAND_LABELS = { bulex: 'Bulex', vaillant: 'Vaillant', sdbg: 'Saunier Duval', 'glow-worm': 'Glow-worm', demirdokum: 'DemirDöküm' } as const;
export const MYVAILLANT_BRAND_NAMES = Object.keys(MYVAILLANT_BRAND_LABELS) as [keyof typeof MYVAILLANT_BRAND_LABELS, ...(keyof typeof MYVAILLANT_BRAND_LABELS)[]];
const MYVAILLANT_COUNTRY_LABELS = {
    albania: 'Albania', austria: 'Austria', belgium: 'Belgium', bosnia: 'Bosnia and Herzegovina', bulgaria: 'Bulgaria', croatia: 'Croatia',
    cyprus: 'Cyprus', czechrepublic: 'Czechia', denmark: 'Denmark', estonia: 'Estonia', finland: 'Finland', france: 'France', georgia: 'Georgia',
    germany: 'Germany', greece: 'Greece', hungary: 'Hungary', ireland: 'Ireland', italy: 'Italy', kosovo: 'Kosovo', latvia: 'Latvia',
    lithuania: 'Lithuania', luxembourg: 'Luxembourg', moldavia: 'Moldova', netherlands: 'Netherlands', 'new-zealand': 'New Zealand',
    'north-macedonia': 'North Macedonia', norway: 'Norway', poland: 'Poland', portugal: 'Portugal', romania: 'Romania', serbia: 'Serbia',
    slovakia: 'Slovakia', slovenia: 'Slovenia', spain: 'Spain', sweden: 'Sweden', switzerland: 'Switzerland', turkiye: 'Türkiye',
    ukraine: 'Ukraine', unitedkingdom: 'United Kingdom', uzbekistan: 'Uzbekistan',
} as const;
export const MYVAILLANT_COUNTRY_NAMES = Object.keys(MYVAILLANT_COUNTRY_LABELS) as [keyof typeof MYVAILLANT_COUNTRY_LABELS, ...(keyof typeof MYVAILLANT_COUNTRY_LABELS)[]];

// The configuration each built-in driver takes, with its credential fields marked (decision §12).
// A separate entry point (`@casa/drivers/config`), so that the driver Worker does not carry zod.
// The built-in manifests (`manifests.ts`) hold these as `configSchema`.

const refreshInterval = z.number().positive().optional().describe('seconds between refreshes');
const port = z.number().int().min(1).max(65_535).optional();

export const DRIVER_CONFIG_SCHEMAS = {
    homewizardp1: z.object({ host: z.string().min(1), port, refreshInterval }),
    anna: z.object({
        host: z.string().min(1),
        port,
        username: z.string().optional(),
        password: secret(z.string()).optional(),
        refreshInterval,
    }),
    airgradient: z.object({
        host: z.string().min(1),
        altitude: z.number().optional().describe('metres, for the CO2 correction'),
        refreshInterval,
    }),
    hubitat: z.object({
        host: z.string().min(1).describe('the hub, for example 192.168.1.20'),
        appId: z.union([z.number().int().positive(), z.string().regex(/^\d+$/)]).describe('the id of the Maker API app'),
        token: secret(z.string().min(1)).describe('the Maker API access token'),
        refreshInterval,
    }),
    influx: z.object({
        url: z.string().regex(/^https?:\/\//i),
        org: z.string().optional(),
        database: z.string().optional(),
        token: secret(z.string()).optional(),
        refreshInterval,
    }),
    evcc: z.object({
        url: z.string().regex(/^https?:\/\//i).describe('the instance, for example http://192.168.1.30:7070'),
        password: secret(z.string()).optional().describe('the admin password, only where EVCC asks for it'),
        refreshInterval,
    }),
    // Titles: the form would make "Lat" and "Api key" of the names. `meta` replaces, so the key's says `secret` again.
    openweathermap: z.object({
        apiKey: secret(z.string().min(1)).meta({ secret: true, title: 'API key', description: 'an OpenWeatherMap key with the One Call API 4.0 subscription' }),
        lat: z.number().min(-90).max(90).meta({ title: 'Latitude', description: 'of the place, in decimal degrees, such as 50.85' }),
        lon: z.number().min(-180).max(180).meta({ title: 'Longitude', description: 'of the place, in decimal degrees, such as 4.35' }),
        units: z.enum(['metric', 'imperial']).optional().meta({ title: 'Units', description: 'metric (°C, km/h, mm) when left out, or imperial (°F, mph, in)' }),
        lang: z.string().optional().meta({ title: 'Language', description: 'of descriptions and warnings, such as nl; the device’s when left out' }),
        currentEvery: z.number().positive().optional().meta({ title: 'Current conditions every', description: 'minutes; 15 when left out' }),
        forecastEvery: z.number().positive().optional().meta({ title: 'Forecasts every', description: 'minutes; 30 when left out' }),
    }),
    homey: z.object({
        host: z.string().min(1).describe('the Homey, for example 192.168.1.50'),
        key: secret(z.string().min(1)).describe('an API key, made in Homey’s settings under API keys'),
        refreshInterval,
    }),
    homeassistant: z.object({
        host: z.string().min(1).describe('Home Assistant, for example 192.168.1.10, or its URL, such as https://ha.example.com'),
        port: port.describe('8123 when left out; not used with a URL'),
        token: secret(z.string().min(1)).describe('a long-lived access token, made in your Home Assistant profile under Security'),
        refreshInterval,
    }),
    hue: z.object({
        host: z.string().min(1).describe('the bridge, for example 192.168.1.40'),
        key: secret(z.string()).optional().describe('leave it empty, press the bridge’s link button, then test the connection: it pairs and fills it in'),
        bridgeId: z.string().optional().describe('the bridge’s id, which its certificate names; the connection’s test fills it in'),
        refreshInterval,
    }),
    uconnect: z.object({
        brand: z.enum(UCONNECT_BRAND_NAMES).optional().meta({ title: 'Brand and region', description: 'the brand app the account logs in to; Fiat, Europe when left out. Its cars of every brand are listed', 'x-labels': UCONNECT_BRAND_LABELS }),
        email: z.string().min(1).meta({ title: 'Email', description: 'the login of the brand’s app (Fiat, Jeep, Alfa Romeo, …)' }),
        password: secret(z.string().min(1)).meta({ secret: true, title: 'Password', description: 'of that login' }),
        pin: secret(z.string().regex(/^\d{4}$/)).optional().meta({ secret: true, title: 'PIN', description: 'the app’s four-digit PIN, for commands (charging, climate); readings only when left out' }),
        refreshInterval: z.number().positive().optional().meta({ title: 'Refresh every', description: 'seconds; 600 when left out' }),
    }),
    // The myVAILLANT cloud (myvaillant-driver spec MV-1). The country only where the brand logs in per country.
    myvaillant: z.object({
        brand: z.enum(MYVAILLANT_BRAND_NAMES).optional().meta({ title: 'Brand', description: 'whose app the account logs in to; Bulex when left out', 'x-labels': MYVAILLANT_BRAND_LABELS }),
        country: z.enum(MYVAILLANT_COUNTRY_NAMES).optional().meta({ title: 'Country', description: 'where the account is registered; Vaillant and Saunier Duval only', 'x-labels': MYVAILLANT_COUNTRY_LABELS }),
        email: z.string().min(1).meta({ title: 'Email', description: 'the login of the brand’s app (MiGo Link, myVAILLANT, …)' }),
        password: secret(z.string().min(1)).meta({ secret: true, title: 'Password', description: 'of that login' }),
        quickVetoHours: z.number().min(0.5).max(24).optional().meta({ title: 'Temporary set point for', description: 'hours, when a zone’s temperature is set on its schedule; 3 when left out' }),
        refreshInterval: z.number().positive().optional().meta({ title: 'Refresh every', description: 'seconds; 120 when left out (the cloud limits requests)' }),
    }),
    // ebusd on the LAN (ebusd-driver spec EB-1). Names go into ebusd's command line: no spaces.
    ebusd: z.object({
        host: z.string().min(1).describe('where ebusd runs, for example 192.168.1.20'),
        port: port.describe('ebusd’s TCP port; 8888 when left out'),
        circuit: z.string().regex(/^[A-Za-z0-9_.-]+$/).optional().describe('the controller’s circuit in ebusd, such as ctlv2; found when left out'),
        refreshInterval: z.number().positive().optional().meta({ title: 'Refresh every', description: 'seconds; 60 when left out' }),
    }),
    // A Renson Healthbox 3.0 on the LAN (renson-driver spec RE-1).
    renson: z.object({
        host: z.string().min(1).describe('the Healthbox, for example 192.168.1.60'),
        port,
        key: secret(z.string()).optional().meta({ secret: true, title: 'API key', description: 'the Healthbox’s API key, from Renson; left out, none is sent' }),
        refreshInterval,
    }),
    // Tilez Cloud's weather (public-services.md SV-15): OpenWeatherMap's place, units and pace, without its key.
    'casa-weather': z.object({
        server: z.string().regex(/^https?:\/\//i).meta({ title: 'Tilez Cloud', description: 'Tilez Cloud’s address, such as https://casa-cloud.app' }),
        serviceKey: secret(z.string().min(1)).meta({ secret: true, title: 'Service key', description: 'made on Tilez Cloud’s admin page, under Service keys' }),
        lat: z.number().min(-90).max(90).meta({ title: 'Latitude', description: 'of the place, in decimal degrees, such as 50.85' }),
        lon: z.number().min(-180).max(180).meta({ title: 'Longitude', description: 'of the place, in decimal degrees, such as 4.35' }),
        units: z.enum(['metric', 'imperial']).optional().meta({ title: 'Units', description: 'metric (°C, km/h, mm) when left out, or imperial (°F, mph, in)' }),
        lang: z.string().optional().meta({ title: 'Language', description: 'of descriptions and warnings, such as nl; the device’s when left out' }),
        currentEvery: z.number().positive().optional().meta({ title: 'Current conditions every', description: 'minutes; 15 when left out' }),
        forecastEvery: z.number().positive().optional().meta({ title: 'Forecasts every', description: 'minutes; 30 when left out' }),
    }),
    // Tilez Cloud's prices (public-services.md SV-17, SV-18, SV-21). `slots` has no default (QSV-14).
    'casa-prices': z.object({
        server: z.string().regex(/^https?:\/\//i).meta({ title: 'Tilez Cloud', description: 'Tilez Cloud’s address, such as https://casa-cloud.app' }),
        serviceKey: secret(z.string().min(1)).meta({ secret: true, title: 'Service key', description: 'made on Tilez Cloud’s admin page, under Service keys' }),
        zone: z.enum(Object.keys(ENERGY_PRICE_ZONES) as [keyof typeof ENERGY_PRICE_ZONES, ...(keyof typeof ENERGY_PRICE_ZONES)[]]).meta({ title: 'Zone', description: 'the market’s bidding zone', 'x-labels': ENERGY_PRICE_ZONES }),
        slots: z.enum(['hour', 'quarter']).meta({
            title: 'Prices per',
            description: 'as your contract prices it; its tariff sheet says: “Belpex H” or “uur” per hour, “Epex 15’”, “eSpot_15” or “kwartier” per quarter-hour',
            'x-labels': { hour: 'Hour', quarter: 'Quarter-hour' },
        }),
        multiplier: z.number().optional().meta({ title: 'Multiplier', description: 'of the market price in EUR/kWh; 1 when left out. A tariff sheet’s factor per €/MWh in c€/kWh, times 10: “0.11 × Belpex” is 1.1 here' }),
        markup: z.number().optional().meta({ title: 'Markup', description: 'EUR/kWh added to it; 0 when left out' }),
        gridCosts: z.number().optional().meta({ title: 'Grid costs and levies', description: 'EUR/kWh added to it; 0 when left out' }),
        vat: z.number().min(0).max(100).optional().meta({ title: 'VAT', description: '% on the whole; 0 when left out' }),
        refreshInterval,
    }),
    // The demo home (demo-driver spec DM-4, DM-5): nothing is required.
    demo: z.object({
        seed: z.string().optional().meta({ title: 'Seed', description: 'another seed gives another home’s days; "casa" when left out' }),
        clock: z.string().regex(/^(live|([01]\d|2[0-3]):[0-5]\d)$/, 'live, or a time of day such as 12:30').optional().meta({ title: 'Clock', description: 'live, or a time of day such as 12:30, for screenshots' }),
        season: z.enum(['live', 'summer', 'winter']).optional().meta({ title: 'Season', description: 'live, or a summer or a winter day, for screenshots' }),
        refreshInterval,
    }),
} as const;

export type BuiltInDriverName = keyof typeof DRIVER_CONFIG_SCHEMAS;

/**
 * A parameter whose values the driver lists (`Driver.choices`, M-2), with the parameters it waits
 * for (`x-dependsOn`, spec §5.6 F-4). zod's `meta` replaces, so everything goes in at once.
 */
/** `none`: what leaving it out means, in words, where it is not nothing ("The whole device"). */
const listed = (title: string, description: string, dependsOn?: readonly string[], none?: string) : z.ZodString =>
    z.string().meta({ title, description, 'x-choices': true, ...(dependsOn ? { 'x-dependsOn': [...dependsOn] } : {}), ...(none ? { 'x-none': none } : {}) });
/** Polled drivers tell their readings apart by the `device` tag (`selectReadings`). */
const byDevice = z.literal('device').meta({ title: 'Tag', description: 'readings are told apart by their device' });
const refreshEvery = z.number().positive().optional().meta({ title: 'Refresh every', description: 'seconds; the connection\'s setting when left out' });

/** One of InfluxDB's two ways to a value (F-3): a measurement and a field. */
const influxByField = z.looseObject({
    measurement: listed('Measurement', 'what is measured', []),
    field: listed('Field', 'which of its fields', ['measurement']),
    func: z.enum(['last', 'mean', 'min', 'max', 'sum']).optional().meta({ title: 'Function', description: 'over the period, or each interval; the last value when left out, the mean when grouped', 'x-dependsOn': ['field'] }),
    period: z.string().optional().meta({ title: 'Period', description: 'a history over this long, such as 24h or 7d; a single value when left out', 'x-dependsOn': ['field'] }),
    grouping: z.string().optional().meta({ title: 'Group by', description: 'one value per interval, such as 1h or 1d, a gap taking the one before', 'x-dependsOn': ['field'] }),
    tz: z.string().optional().meta({ title: 'Time zone', description: 'where the intervals start, such as Europe/Brussels; UTC when left out', 'x-dependsOn': ['field'] }),
}).meta({ title: 'Measurement and field', description: 'a measurement and one of its fields, as a value or a history' });

/** InfluxDB's other way: a query of its own. */
const influxByQuery = z.looseObject({
    query: z.string().meta({ title: 'Query', description: 'InfluxQL; {{...}} dates are filled in' }),
}).meta({ title: 'Query', description: 'an InfluxQL query of its own' });

/** EVCC's first way (evcc-driver spec EV-4 to EV-8): a part of the state, and a value in it. */
const evccByValue = z.looseObject({
    part: listed('Part', 'the site, the grid, a PV meter, a loadpoint, a vehicle, …', []),
    value: listed('Value', 'one of its values; all of them, as a record, when left out', ['part'], 'All of its values').optional(),
}).meta({ title: 'A value', description: 'any value of the state, such as a loadpoint\'s charge power' });

/** EVCC's other way (EV-9 to EV-12): a tariff's prices. */
const evccByTariff = z.looseObject({
    tariff: listed('Tariff', 'the prices of buying, of feeding in, of the planner; CO\u2082; the solar forecast', []),
    read: z
        .enum(['rates', 'current', 'next', 'lowest', 'highest', 'average'])
        .optional()
        .meta({
            title: 'Read',
            description: 'rates: the prices to come, for a graph; current: the price now; next: the next slot\'s; lowest, highest, average: of the prices to come. The prices to come when left out',
            'x-dependsOn': ['tariff'],
        }),
}).meta({ title: 'Prices', description: 'a tariff: the price now, the prices to come, the lowest' });

/** A weather source (OpenWeatherMap's, and the Tilez Hub's): the current conditions or the forecast. */
const weatherSource = z.object({
    read: listed('Read', 'the current conditions, or the forecast for the forecast tile', []),
    // Only for the current conditions: a forecast is read whole (issue #19).
    value: z.string().optional().meta({ title: 'Value', description: 'which of the current conditions; all of them, as a record, when left out', 'x-choices': true, 'x-dependsOn': ['read'], 'x-showWhen': { read: ['current'] }, 'x-none': 'All current conditions' }),
});

/** A zod object as a JSON Schema to put inside another, without the `$schema` of a document. */
function inner(schema: z.ZodType) : Record<string, unknown> {
    const { $schema: _schema, ...rest } = z.toJSONSchema(schema) as Record<string, unknown>;

    return rest;
}

/**
 * The parameters of each built-in driver's data sources, in the order they are chosen (M-1,
 * dashboard-editor spec §6.1), with what each waits for and, for InfluxDB, its two ways
 * (§5.6). The manifests hold these as `sourceSchema`.
 */
export const DRIVER_SOURCE_SCHEMAS = {
    homewizardp1: z.object({
        tag: byDevice,
        tagValue: listed('Meter', 'the electricity meter, or the water meter connected to it', []),
        attribute: listed('Reading', 'which of the meter\'s values', ['tagValue']),
    }),
    anna: z.object({
        tag: byDevice,
        tagValue: listed('Device', 'the thermostat, the water heater or the heat pump', []),
        // Left out: the device whole; the thermostat's is the thermostat role (device-roles.md DR-2).
        attribute: listed('Reading', 'which of the device\'s values; all of them, and the thermostat as one, when left out', ['tagValue'], 'The whole device').optional(),
    }),
    airgradient: z.object({
        attribute: listed('Reading', 'which measurement, as a running average, or its trend', []),
    }),
    hubitat: z.object({
        device: listed('Device', 'one of the hub\'s devices that the Maker API app includes', []),
        attribute: listed('Attribute', 'which of the device\'s attributes; all of them when left out', ['device'], 'The whole device').optional(),
        refreshInterval: refreshEvery,
    }),
    // "Refresh every" beside the two ways (`oneOf`, which zod cannot say of an object's own fields).
    // Loose objects: the ways' fields and those beside them are one object's, which a closed one would refuse.
    influx: z.looseObject({
        refreshInterval: refreshEvery,
    }).meta({ oneOf: [inner(influxByField), inner(influxByQuery)] }),
    evcc: z.looseObject({
        refreshInterval: refreshEvery,
    }).meta({ oneOf: [inner(evccByValue), inner(evccByTariff)] }),
    openweathermap: weatherSource,
    homey: z.object({
        device: listed('Device', 'one of the Homey’s devices, with its room', []),
        capability: listed('Value', 'which of the device’s values; all of them, and a light’s state, when left out', ['device'], 'The whole device').optional(),
        refreshInterval: refreshEvery,
    }),
    homeassistant: z.object({
        entity: listed('Entity', 'one of Home Assistant’s entities, with its area', []),
        attribute: listed('Attribute', 'one of the entity’s attributes; its state when left out, and a light or a thermostat whole', ['entity'], 'Its state').optional(),
        refreshInterval: refreshEvery,
    }),
    hue: z.object({
        kind: listed('What', 'a light, a room, a zone or a sensor', []),
        item: listed('Which', 'by its name on the bridge', ['kind']),
        value: listed('Value', 'what to read of it; the whole light for a light, a room or a zone when left out', ['item'], 'The whole light').optional(),
        refreshInterval: refreshEvery,
    }),
    demo: z.object({
        device: listed('Device', 'one of the demo home’s devices, by room', []),
        value: listed('Value', 'one of the device’s values; all of them, as a record, when left out', ['device'], 'The whole device').optional(),
        history: z
            .enum(['1h', '24h', '7d', 'today', 'yesterday'])
            .optional()
            .meta({ title: 'History', description: 'a number’s past, for a graph or a sparkline: the last hour, 24 hours or 7 days, today since midnight, or yesterday; the value now when left out', 'x-dependsOn': ['value'] }),
        refreshInterval: refreshEvery,
    }),
    // The same as OpenWeatherMap's, so that a connection can switch drivers without changing its tiles (SV-15).
    'casa-weather': weatherSource,
    'casa-prices': z.object({
        read: z.enum(['rates', 'current', 'next', 'lowest', 'highest', 'average']).meta({
            title: 'Read',
            description: 'the prices, for a graph or a sparkline; the price now; the next slot’s; the lowest, the highest or the average',
            'x-labels': { rates: 'The prices', current: 'The price now', next: 'The next price', lowest: 'The lowest', highest: 'The highest', average: 'The average' },
        }),
        // Left out: the prices to come, as EVCC's tariffs read them.
        window: z.enum(['today', 'tomorrow', 'todayAndTomorrow']).optional().meta({
            title: 'Over',
            description: 'the prices to come when left out; or today’s, tomorrow’s (once published, early afternoon), or both days',
            'x-labels': { today: 'Today', tomorrow: 'Tomorrow', todayAndTomorrow: 'Today and tomorrow' },
            'x-none': 'The prices to come',
            'x-showWhen': { read: ['rates', 'lowest', 'highest', 'average'] },
        }),
        refreshInterval: refreshEvery,
    }),
    uconnect: z.object({
        vehicle: listed('Car', 'one of the account’s cars', []),
        value: listed('Value', 'the battery, the range, fuel, climate, …; all of them, as a record, when left out', ['vehicle'], 'The whole car').optional(),
    }),
    myvaillant: z.object({
        device: listed('Device', 'a zone, the hot water or the system of one of the account’s homes', []),
        value: listed('Value', 'one of its values; all of them, and a zone as a thermostat, when left out', ['device'], 'The whole device').optional(),
        refreshInterval: refreshEvery,
    }),
    ebusd: z.object({
        device: listed('Device', 'a zone, the hot water or the system of the controller; or any circuit ebusd knows', []),
        value: listed('Value', 'one of its values; all of them, and a zone as a thermostat, when left out', ['device'], 'The whole device').optional(),
        refreshInterval: refreshEvery,
    }),
    renson: z.object({
        tag: byDevice,
        tagValue: listed('Room', 'one of the Healthbox’s rooms', []),
        attribute: listed('Reading', 'the air flow, temperature, humidity, CO₂, VOC, or the boost; all of them, as a record, when left out', ['tagValue'], 'The whole room').optional(),
    }),
} as const satisfies Record<BuiltInDriverName, z.ZodObject>;

/**
 * Seconds between refreshes of a source that sets no `refreshInterval` of its own. P1 and Anna:
 * what the legacy drivers meant (they kept it in a field they never read and polled every 30 s).
 * AirGradient: fixed, because its averages and trends count samples (the legacy service also
 * ignored the dashboard's setting).
 */
const DEFAULT_REFRESH_S: Readonly<Record<BuiltInDriverName, number>> = { homewizardp1: 5, anna: 15, airgradient: 30, hubitat: 15, influx: 5, evcc: 10, homey: 15, homeassistant: 15, hue: 2, openweathermap: 900, uconnect: 600, myvaillant: 120, ebusd: 60, renson: 5, demo: 5, 'casa-weather': 900, 'casa-prices': 60 };
const FIXED_REFRESH: ReadonlySet<BuiltInDriverName> = new Set(['airgradient']);

/** The refresh interval of a source, in seconds: its own, the connection's, or the driver's default. */
export function refreshIntervalS(driver: string, config: Readonly<Record<string, unknown>>, sourceInterval?: number) : number {
    const configured = typeof config.refreshInterval === 'number' && config.refreshInterval > 0 ? config.refreshInterval : undefined;
    if (!isBuiltInDriverName(driver)) {
        return sourceInterval ?? configured ?? 30;
    }
    if (FIXED_REFRESH.has(driver)) {
        return DEFAULT_REFRESH_S[driver];
    }

    return sourceInterval ?? configured ?? DEFAULT_REFRESH_S[driver];
}

export function isBuiltInDriverName(driver: string) : driver is BuiltInDriverName {
    return Object.hasOwn(DRIVER_CONFIG_SCHEMAS, driver);
}

/** The config keys of a driver that hold credentials; none for a driver that is not built in. */
export function driverSecretKeys(driver: string) : string[] {
    return isBuiltInDriverName(driver) ? secretKeys(DRIVER_CONFIG_SCHEMAS[driver]) : [];
}

/** Problems with a connection's config, as messages; empty when it is valid. */
export function checkDriverConfig(driver: string, config: unknown) : string[] {
    if (!isBuiltInDriverName(driver)) {
        return [`no built-in driver named "${driver}"`];
    }

    const result = DRIVER_CONFIG_SCHEMAS[driver].safeParse(config);

    return result.success ? [] : result.error.issues.map((issue) => `${issue.path.map(String).join('.') || 'config'}: ${issue.message}`);
}
